import { memo, useRef, useState, useCallback, useEffect } from 'react';
import { GalleryCard } from '../GalleryCard';
import type { GalleryProfileCard } from '../../../types/gallery';
import './RowLayout.css';

// ==========================================
// TYPES
// ==========================================

interface RowLayoutProps {
  profiles: GalleryProfileCard[];
  onProfileClick: (profileId: string, position: number) => void;
  onSwipeStart: (profileId: string) => void;
  selectedProfileId?: string;
  cardVariant?: 'icon' | 'compact';
  showArrows?: boolean;
  enableSnap?: boolean;
  className?: string;
}

// ==========================================
// COMPONENT
// ==========================================

function RowLayoutComponent({
  profiles,
  onProfileClick,
  onSwipeStart,
  selectedProfileId,
  cardVariant = 'compact',
  showArrows = true,
  enableSnap = true,
  className = '',
}: RowLayoutProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Check scroll position to show/hide arrows
  const updateScrollState = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  // Initialize and update on resize
  useEffect(() => {
    updateScrollState();

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', updateScrollState);
      window.addEventListener('resize', updateScrollState);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', updateScrollState);
      }
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, profiles.length]);

  // Scroll by a page width
  const scrollByPage = useCallback((direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = container.clientWidth * 0.8;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  }, []);

  if (profiles.length === 0) {
    return null;
  }

  const rowClass = [
    'gallery-row',
    `gallery-row--${cardVariant}`,
    enableSnap ? 'gallery-row--snap' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="gallery-row-container">
      {/* Left Arrow */}
      {showArrows && canScrollLeft && (
        <button
          className="gallery-row__arrow gallery-row__arrow--left"
          onClick={() => scrollByPage('left')}
          aria-label="Scroll left"
        >
          ‹
        </button>
      )}

      {/* Scrollable Row */}
      <div ref={scrollContainerRef} className={rowClass}>
        {profiles.map((profile, index) => (
          <div key={profile.profileId} className="gallery-row__item">
            <GalleryCard
              profile={profile}
              variant={cardVariant}
              onClick={() => onProfileClick(profile.profileId, index)}
              onSwipeStart={() => onSwipeStart(profile.profileId)}
              isSelected={profile.profileId === selectedProfileId}
            />
          </div>
        ))}
      </div>

      {/* Right Arrow */}
      {showArrows && canScrollRight && (
        <button
          className="gallery-row__arrow gallery-row__arrow--right"
          onClick={() => scrollByPage('right')}
          aria-label="Scroll right"
        >
          ›
        </button>
      )}
    </div>
  );
}

export const RowLayout = memo(RowLayoutComponent);
